/**
 * Authentication service for Cognito OAuth integration
 */

import { env } from '@/config/env'
import type { CognitoTokenResponse, CognitoUserInfo, UserProfile } from '@/types/auth'

/**
 * Build Cognito hosted UI login URL
 */
export function buildLoginUrl(): string {
  const params = new URLSearchParams({
    client_id: env.cognitoClientId,
    response_type: 'code',
    scope: 'openid',  // Federate only supports 'openid' scope
    redirect_uri: env.cognitoRedirectUri,
  })
  
  return `https://${env.cognitoDomain}/oauth2/authorize?${params.toString()}`
}

/**
 * Build Cognito logout URL
 */
export function buildLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: env.cognitoClientId,
    logout_uri: env.cognitoRedirectUri.replace('/callback', ''),
  })
  
  return `https://${env.cognitoDomain}/logout?${params.toString()}`
}

/**
 * Handle OAuth callback by calling backend endpoint
 * Backend will exchange code for tokens and create/update user profile
 * 
 * Note: Current backend implementation is a placeholder that expects
 * authentication via middleware. This will need backend updates to
 * properly exchange the code for tokens.
 */
export async function handleAuthCallback(code: string): Promise<{
  tokens: CognitoTokenResponse
  user: UserProfile
}> {
  // For now, we need to exchange code for tokens ourselves
  // then call backend with the access token
  // TODO: Update when backend implements proper code exchange
  
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.cognitoClientId,
    code,
    redirect_uri: env.cognitoRedirectUri,
  })

  const tokenResponse = await fetch(`https://${env.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!tokenResponse.ok) {
    const error = await tokenResponse.text()
    throw new Error(`Token exchange failed: ${error}`)
  }

  const tokens: CognitoTokenResponse = await tokenResponse.json()

  // Now call backend callback with access token to create/update user
  // Backend will use access token to fetch user info from Cognito /oauth2/userInfo endpoint
  const callbackResponse = await fetch(`${env.apiBaseUrl}/api/v1/auth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokens.access_token}`,
    },
  })

  if (!callbackResponse.ok) {
    const error = await callbackResponse.text()
    throw new Error(`Auth callback failed: ${error}`)
  }

  const data = await callbackResponse.json()
  
  return {
    tokens: tokens,
    user: data.user,
  }
}

/**
 * Refresh access token using refresh token via backend
 */
export async function refreshAccessToken(refreshToken: string): Promise<CognitoTokenResponse> {
  const response = await fetch(`${env.apiBaseUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Token refresh failed: ${error}`)
  }

  return response.json()
}

/**
 * Fetch user info from Cognito (kept for potential future use)
 * Note: In production, user info comes from backend callback
 */
export async function fetchUserInfo(accessToken: string): Promise<CognitoUserInfo> {
  const response = await fetch(`https://${env.cognitoDomain}/oauth2/userInfo`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Failed to fetch user info: ${error}`)
  }

  return response.json()
}

/**
 * Mock authentication for local development
 */
export function getMockAuth() {
  const mockTokens: CognitoTokenResponse = {
    access_token: 'mock-access-token',
    id_token: 'mock-id-token',
    refresh_token: 'mock-refresh-token',
    expires_in: 3600,
    token_type: 'Bearer',
  }

  const mockUser: UserProfile = {
    user_id: 'dev-user-001',
    email: 'gili@amazon.com',
    name: 'Gili Nachum',
    cognito_sub: 'dev-cognito-sub-001',
    role: 'Developer',
    department: 'Engineering',
    region: 'US',
  }

  return { tokens: mockTokens, user: mockUser }
}
